import React, { useState, useEffect } from 'react';
import { X, Send, Paperclip, User, UserPlus, FileText, ArrowLeft, Download, MessageSquare, CheckSquare, FileCheck, ListTodo } from 'lucide-react';
import api from '../api';
import SurveyBuilder from './SurveyBuilder';

const MessageModal = ({ date, onClose, initialType = 'individual' }) => {
  const [recipientType, setRecipientType] = useState(initialType);
  const [formData, setFormData] = useState({
    recipient: '',
    materia: '',
    subject: initialType === 'evento' ? 'Feriado: ' : '',
    text: '',
    pdf: null
  });
  const [messageSent, setMessageSent] = useState(false);
  const [showSurveyBuilder, setShowSurveyBuilder] = useState(false);
  const [surveyData, setSurveyData] = useState({
      title: '',
      questions: []
  });

  const [users, setUsers] = useState([]);
  const [diplomas, setDiplomas] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, diplomasRes] = await Promise.all([
        api.get('users/'),
        api.get('diplomas/')
      ]);
      setUsers(usersRes.data);
      setDiplomas(diplomasRes.data);
    } catch (err) {
      console.error('Error fetching data for message modal:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentUser = sessionStorage.getItem('userEmail') || "usuario@admin.com";

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = [
        "application/pdf", 
        "application/msword", 
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];
    if (file && allowedTypes.includes(file.type)) {
      setFormData({ ...formData, pdf: file });
    } else {
      alert("Por favor seleccione un archivo PDF o Word (.doc, .docx)");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
        let surveyId = null;

        if (showSurveyBuilder && surveyData.questions.length > 0) {
            // Validate survey has questions and options
            const isValid = surveyData.questions.every(q => q.text.trim() !== '' && q.options.length >= 2);
            if (!isValid) {
                alert("La encuesta requiere al menos una pregunta válida con al menos dos opciones.");
                setLoading(false);
                return;
            }
            try {
                const surveyRes = await api.post('surveys/', {
                    title: surveyData.title || formData.subject,
                    questions_json: surveyData.questions
                });
                surveyId = surveyRes.data.id;
            } catch (err) {
                console.error("Error creating survey:", err);
                alert("Error al crear la encuesta adjunta.");
                setLoading(false);
                return;
            }
        }

        const fd = new FormData();
        fd.append('recipient_type', recipientType);
        fd.append('recipient_id', recipientType === 'individual' ? formData.recipient : (recipientType === 'evento' ? 'general' : formData.materia));
        fd.append('subject', formData.subject);
        fd.append('text', formData.text);
        fd.append('date', date.toISOString().split('T')[0]);
        
        if (formData.pdf) {
            fd.append('pdf_file', formData.pdf);
        }
        if (surveyId) {
            fd.append('survey_id', surveyId);
        }

        await api.post('messages/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        setMessageSent(true);
        setTimeout(() => {
            onClose(true);
        }, 1500);
    } catch (error) {
        console.error('Error sending message:', error);
        alert("Error al enviar el mensaje. Revisé su conexión.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
        <div className="d-flex align-items-center mb-4">
             <button onClick={() => onClose(false)} className="btn btn-glass btn-sm me-3">
                <ArrowLeft size={18} />
             </button>
             <h4 className="mb-0">{recipientType === 'evento' ? 'Nuevo Evento General' : 'Nuevo Mensaje / Tarea'}</h4>
        </div>

        {messageSent ? (
            <div className="text-center py-5">
                <div className="mb-3"><Send size={48} className="text-success animate-bounce" /></div>
                <h5 className="text-success">¡Mensaje enviado con éxito!</h5>
                <p className="text-muted">Aparecerá en el calendario de los destinatarios.</p>
            </div>
        ) : (
            <form onSubmit={handleSubmit}>
                {recipientType !== 'evento' && (
                  <div className="mb-4">
                    <label className="form-label text-muted small fw-bold">DESTINATARIO</label>
                    <div className="d-flex gap-2 mb-3">
                        <button 
                            type="button" 
                            className={`btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2 ${recipientType === 'individual' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setRecipientType('individual')}
                        >
                            <User size={16} /> Individual
                        </button>
                        <button 
                            type="button" 
                            className={`btn btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2 ${recipientType === 'materia' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setRecipientType('materia')}
                        >
                            <UserPlus size={16} /> Toda un Aula
                        </button>
                    </div>

                    {recipientType === 'individual' ? (
                        <>
                            <input 
                                type="email" 
                                className="form-control" 
                                placeholder="Escriba o seleccione un correo..."
                                list="recipientEmails"
                                value={formData.recipient}
                                onChange={e => setFormData({...formData, recipient: e.target.value})}
                                required 
                            />
                            <datalist id="recipientEmails">
                                {users.map(u => (
                                    <option key={u.email} value={u.email}>{u.first_name || u.last_name ? `${u.first_name} ${u.last_name}` : u.email}</option>
                                ))}
                            </datalist>
                        </>
                    ) : (
                        <select 
                            className="form-select"
                            value={formData.materia}
                            onChange={e => setFormData({...formData, materia: e.target.value})}
                            required
                        >
                            <option value="">Seleccione la Materia...</option>
                            {diplomas.flatMap(d => d.subjects || []).map(m => (
                                <option key={m.id} value={m.name}>{m.name}</option>
                            ))}
                        </select>
                    )}
                  </div>
                )}

                <div className="mb-4">
                    <label className="form-label text-muted small fw-bold">ASUNTO</label>
                    <input 
                        type="text" 
                        className="form-control fw-bold border-primary border-opacity-25" 
                        placeholder="Motivo principal del mensaje..."
                        value={formData.subject}
                        onChange={e => setFormData({...formData, subject: e.target.value})}
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="form-label text-muted small fw-bold">MENSAJE / TAREA</label>
                    <textarea 
                        className="form-control" 
                        rows="4" 
                        placeholder="Escribe el contenido detallado aquí..."
                        value={formData.text}
                        onChange={e => setFormData({...formData, text: e.target.value})}
                        required
                    ></textarea>
                </div>

                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label text-muted small fw-bold mb-0">ENCUESTA INTERACTIVA</label>
                        <button 
                            type="button" 
                            className={`btn btn-sm ${showSurveyBuilder ? 'btn-danger' : 'btn-outline-info'}`}
                            onClick={() => setShowSurveyBuilder(!showSurveyBuilder)}
                        >
                            {showSurveyBuilder ? 'Quitar Encuesta' : <><ListTodo size={14} className="me-1" /> Adjuntar Encuesta</>}
                        </button>
                    </div>
                    {showSurveyBuilder && (
                         <div className="animate-fade-in">
                            <SurveyBuilder surveyData={surveyData} setSurveyData={setSurveyData} />
                         </div>
                    )}
                </div>

                <div className="mb-4">
                    <label className="form-label text-muted small fw-bold">ADJUNTO (PDF o Word)</label>
                    <div 
                        className={`file-upload-box p-3 text-center border-dashed cursor-pointer hover-bg-light rounded-3 transition-all ${formData.pdf ? 'border-success bg-success bg-opacity-10' : 'border-primary border-opacity-25'}`}
                        onClick={() => document.getElementById('msg-pdf').click()}
                    >
                        <input 
                            type="file" 
                            className="d-none" 
                            id="msg-pdf" 
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" 
                            onChange={handleFileChange} 
                        />
                        <div className="mb-0 w-100">
                            {formData.pdf ? (
                                <div className="animate-pop-in d-flex flex-column align-items-center">
                                    <div className="badge bg-success text-white mb-2 d-flex align-items-center gap-1"><CheckSquare size={14} /> Archivo Cargado</div>
                                    <small className="text-muted d-block text-truncate" style={{maxWidth: '200px'}}>{formData.pdf.name}</small>
                                </div>
                            ) : (
                                <><Paperclip size={20} className="text-primary me-2" /><span className="text-primary fw-medium">Haga clic para adjuntar (PDF/Word)</span></>
                            )}
                        </div>
                    </div>
                </div>

                <button type="submit" className="btn btn-primary-gradient w-100 py-3 fw-bold mt-2" disabled={loading}>
                    <Send size={18} className="me-2" /> {loading ? 'Procesando...' : (recipientType === 'evento' ? 'PUBLICAR EVENTO' : 'ENVIAR MENSAJE')}
                </button>
            </form>
        )}
    </div>
  );
};

export default MessageModal;
